import {useState} from 'react';
import {Link as RouterLink, useOutletContext} from 'react-router-dom';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import TollOutlinedIcon from '@mui/icons-material/TollOutlined';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  ClickIdValue,
  ImpersonationToken,
  useGetUserExtDataByName,
  useIssueImpersonationToken,
  UserAvatar,
  useTopUpCfp,
  useTopUpCoins,
  useTopUpPromo,
  useTopUpReal,
  useUnbanUser,
} from '@entities/user';
import {CurrencyValue, openConfirmation} from '@shared/components';
import {DATETIME_FORMAT, ROUTE__USER_ACCOUNT} from '@shared/constants';
import {formatInTz} from '@shared/utils';
import {
  BanUserDialog,
  GuestAccessDialog,
  openTopUpCfpDialog,
  openTopUpCoinsDialog,
  openTopUpPromoDialog,
  openTopUpRealDialog,
} from '@widgets/user';
import {UserAccountOutletContext} from './types';
import {UserOnboardingsSection} from './UserOnboardingsSection';

export const InformationTab = () => {
  const [isBanDialogOpen, setIsBanDialogOpen] = useState(false);
  const {mutate: unbanUser, isPending: isUnbanning} = useUnbanUser();
  const {user, currency} = useOutletContext<UserAccountOutletContext>();
  const {
    data: clickId,
    isFetching: isLoadingClickId,
    isError: isClickIdError,
  } = useGetUserExtDataByName(user.id, 'sub1');
  let clickIdValue = clickId?.value || '—';
  if (isLoadingClickId) clickIdValue = 'Loading…';
  else if (isClickIdError) clickIdValue = 'Unable to load';

  const {mutate: topUpReal, isPending: isToppingUpReal} = useTopUpReal();
  const {mutate: topUpPromo, isPending: isToppingUpPromo} = useTopUpPromo();
  const {mutate: topUpCoins, isPending: isToppingUpCoins} = useTopUpCoins();
  const {mutate: topUpCfp, isPending: isToppingUpCfp} = useTopUpCfp();

  const {mutate: issueImpersonationToken, isPending: isIssuingToken} =
    useIssueImpersonationToken();
  const [guestAccessToken, setGuestAccessToken] =
    useState<ImpersonationToken | null>(null);

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box display="flex" gap={3} alignItems="flex-start">
        <UserAvatar
          name={user.name}
          photoUrl={user.avatar || undefined}
          size={80}
        />

        <Box display="flex" flexDirection="column" gap={1.5} flex={1}>
          <Box
            display="grid"
            gridTemplateColumns="160px 1fr"
            gap={1}
            alignItems="center"
          >
            <Typography variant="b2regular" color="text.secondary">
              ID
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="b2regular" sx={{fontFamily: 'monospace'}}>
                {user.id}
              </Typography>
              <Tooltip title="Copy ID">
                <IconButton
                  size="small"
                  onClick={() => void navigator.clipboard.writeText(user.id)}
                >
                  <ContentCopyIcon sx={{fontSize: 14}} />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="b2regular" color="text.secondary">
              Username
            </Typography>
            <Typography variant="b2regular">@{user.username}</Typography>

            <Typography variant="b2regular" color="text.secondary">
              Referred by
            </Typography>
            <Typography variant="b2regular">
              {user.referrer ? (
                <Link
                  component={RouterLink}
                  to={ROUTE__USER_ACCOUNT.replace(':id', user.referrer.id)}
                >
                  @{user.referrer.username}
                </Link>
              ) : user.referrer === null ? (
                '—'
              ) : (
                'Unavailable'
              )}
            </Typography>

            <Typography variant="b2regular" color="text.secondary">
              Name
            </Typography>
            <Typography variant="b2regular">{user.name}</Typography>

            <Typography variant="b2regular" color="text.secondary">
              Auth type
            </Typography>
            <Typography variant="b2regular">{user.authType}</Typography>
            <Typography variant="b2regular" color="text.secondary">
              Banned
            </Typography>
            <Chip
              label={user.ban ? 'Yes' : 'No'}
              color={user.ban ? 'error' : 'default'}
              size="small"
              sx={{width: 'fit-content'}}
            />
            {user.ban && (
              <>
                <Typography variant="b2regular" color="text.secondary">
                  Ban details
                </Typography>
                <Box>
                  <Typography
                    variant="b2regular"
                    sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}
                  >
                    {user.ban.comment}
                  </Typography>
                  <Typography variant="b2regular" color="text.secondary">
                    {user.ban.bannedByUsername} |{' '}
                    {formatInTz(user.ban.bannedAt, DATETIME_FORMAT)}
                  </Typography>
                </Box>
              </>
            )}
            <Typography variant="b2regular" color="text.secondary">
              Phone
            </Typography>
            <Typography variant="b2regular">{user.phone || '—'}</Typography>

            <Typography variant="b2regular" color="text.secondary">
              Email
            </Typography>
            <Typography variant="b2regular">{user.email || '—'}</Typography>

            <Typography variant="b2regular" color="text.secondary">
              Country
            </Typography>
            <Typography variant="b2regular">{user.country || '—'}</Typography>

            <Typography variant="b2regular" color="text.secondary">
              Currency
            </Typography>
            <Typography variant="b2regular">
              {currency?.currency ?? '—'}
            </Typography>

            <Typography variant="b2regular" color="text.secondary">
              ClickID
            </Typography>
            <Typography variant="b2regular" sx={{overflowWrap: 'anywhere'}}>
              {!isLoadingClickId && !isClickIdError && clickId?.value ? (
                <ClickIdValue value={clickId.value} />
              ) : (
                clickIdValue
              )}
            </Typography>

            <Typography variant="b2regular" color="text.secondary">
              Verified
            </Typography>
            <Chip
              label={user.isVerified ? 'Yes' : 'No'}
              color={user.isVerified ? 'success' : 'default'}
              size="small"
              sx={{width: 'fit-content'}}
            />

            <Typography variant="b2regular" color="text.secondary">
              Admin
            </Typography>
            <Chip
              label={user.isAdmin ? 'Yes' : 'No'}
              color={user.isAdmin ? 'warning' : 'default'}
              size="small"
              sx={{width: 'fit-content'}}
            />
          </Box>

          <Divider />

          <Box display="flex" alignItems="center" gap={3}>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="b2regular" color="text.secondary">
                Real balance
              </Typography>
              <Typography variant="b1regular" fontWeight={500}>
                <CurrencyValue value={user.fundsReal} />
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="b2regular" color="text.secondary">
                Promo balance
              </Typography>
              <Typography variant="b1regular" fontWeight={500}>
                <CurrencyValue value={user.fundsPromo} />
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="b2regular" color="text.secondary">
                Coins
              </Typography>
              <Typography variant="b1regular" fontWeight={500}>
                {user.coins}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="b2regular" color="text.secondary">
                CFP
              </Typography>
              <Typography variant="b1regular" fontWeight={500}>
                {user.cfp}
              </Typography>
            </Box>

            <Box display="flex" gap={1} ml={2}>
              <IconButton
                title="Top up real balance"
                disabled={isToppingUpReal || !currency}
                onClick={() => {
                  void openTopUpRealDialog({
                    title: 'Top up real balance',
                    label: `Amount (in ${currency?.currency})`,
                  })
                    .then((amount: number) => {
                      topUpReal({username: user.username, amount});
                      return;
                    })
                    .catch(() => {});
                }}
              >
                <AttachMoneyOutlinedIcon />
              </IconButton>

              <IconButton
                title="Top up promo balance"
                disabled={isToppingUpPromo || !currency}
                onClick={() => {
                  void openTopUpPromoDialog({
                    title: 'Top up promo balance',
                    label: `Amount (in ${currency?.currency})`,
                  })
                    .then((amount: number) => {
                      topUpPromo({username: user.username, amount});
                      return;
                    })
                    .catch(() => {});
                }}
              >
                <CardGiftcardOutlinedIcon />
              </IconButton>

              <IconButton
                title="Top up coins"
                disabled={isToppingUpCoins}
                onClick={() => {
                  void openTopUpCoinsDialog({
                    title: 'Top up coins',
                    label: 'Amount (coins)',
                  })
                    .then((coins: number) => {
                      topUpCoins({username: user.username, coins});
                      return;
                    })
                    .catch(() => {});
                }}
              >
                <TollOutlinedIcon />
              </IconButton>

              <IconButton
                title="Top up CFP"
                disabled={isToppingUpCfp}
                onClick={() => {
                  void openTopUpCfpDialog({
                    title: 'Top up CFP',
                    label: 'Amount (CFP)',
                  })
                    .then((amount: number) => {
                      topUpCfp({username: user.username, amount});
                      return;
                    })
                    .catch(() => {});
                }}
              >
                <AccountBalanceOutlinedIcon />
              </IconButton>
            </Box>

            <Button
              variant="outlined"
              startIcon={<LoginOutlinedIcon />}
              disabled={isIssuingToken}
              sx={{ml: 2}}
              onClick={() => {
                issueImpersonationToken(user.id, {
                  onSuccess: setGuestAccessToken,
                });
              }}
            >
              Guest access
            </Button>

            <Button
              color={user.ban ? 'primary' : 'error'}
              sx={{flexShrink: 0}}
              disabled={isUnbanning}
              onClick={() => {
                if (!user.ban) {
                  setIsBanDialogOpen(true);
                  return;
                }
                void openConfirmation({
                  title: `Unban @${user.username}?`,
                  text: 'The user will be able to sign in again.',
                  okButtonText: 'Unban',
                })
                  .then(() => {
                    unbanUser(user.id);
                    return;
                  })
                  .catch(() => {});
              }}
            >
              {user.ban ? 'Unban user' : 'Ban user'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Divider />

      {isBanDialogOpen && (
        <BanUserDialog
          userId={user.id}
          username={user.username}
          onClose={() => setIsBanDialogOpen(false)}
        />
      )}

      <UserOnboardingsSection userId={user.id} />

      <GuestAccessDialog
        token={guestAccessToken}
        onClose={() => setGuestAccessToken(null)}
      />
    </Box>
  );
};
